import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CreateWorkshopInput, WorkshopDto } from '@workshop-hst/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from './Toast';
import {
  GuestSection,
  emptyGuestState,
  guestStateFromDto,
  guestStateToInput,
  type GuestFormState,
} from './GuestSection';
import { TagInput } from './TagInput';
import { Avatar } from './Avatar';

export type WorkshopFormValues = CreateWorkshopInput;

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function defaultStartsAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(13, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export const WORKSHOP_FORM_ID = 'workshop-form';

export function WorkshopForm({
  initial,
  onSubmit,
  submitting,
  submitLabel,
  cancelHref,
  error,
  hideActions = false,
}: {
  initial?: WorkshopDto;
  onSubmit: (values: WorkshopFormValues) => void;
  submitting: boolean;
  submitLabel: string;
  cancelHref: string;
  error?: string | null;
  /**
   * When true, the inline Cancel/Save row at the bottom of the form is hidden.
   * The form still has `id={WORKSHOP_FORM_ID}` so an external button with
   * `form={WORKSHOP_FORM_ID} type="submit"` can submit it from anywhere on
   * the page — used by the edit screen to place Save below the cover image
   * card instead of right under the form fields.
   */
  hideActions?: boolean;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [hostName, setHostName] = useState(initial?.hostName ?? 'Colin');
  const [hostEmail, setHostEmail] = useState(initial?.hostEmail ?? '');
  const [hostHeadshotUrl, setHostHeadshotUrl] = useState(initial?.hostHeadshotUrl ?? null);
  const [startsAtLocal, setStartsAtLocal] = useState(
    initial ? toLocalDateTimeInput(initial.startsAt) : defaultStartsAt(),
  );
  const [durationMin, setDurationMin] = useState(initial?.durationMin ?? 60);
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [guest, setGuest] = useState<GuestFormState>(
    initial ? guestStateFromDto(initial.guest) : emptyGuestState(),
  );

  // Pull the union of existing tags off the admin workshop list — used to
  // power typeahead so admins don't accidentally create "Growth" + "growth"
  // as separate tags. (Workshop tags are already lowercased on save.)
  const allWorkshops = useQuery({
    queryKey: ['admin', 'workshops'],
    queryFn: () => api.admin.listWorkshops(),
    staleTime: 60_000,
  });
  const tagSuggestions = useMemo(() => {
    const seen = new Set<string>();
    for (const w of allWorkshops.data?.workshops ?? []) {
      for (const t of w.tags) seen.add(t.toLowerCase());
    }
    return Array.from(seen).sort();
  }, [allWorkshops.data]);

  return (
    <form
      id={WORKSHOP_FORM_ID}
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title,
          description,
          hostName,
          hostEmail: hostEmail.trim() || null,
          hostHeadshotUrl,
          startsAt: new Date(startsAtLocal).toISOString(),
          durationMin,
          tags: tags.slice(0, 6),
          guest: guestStateToInput(guest),
        });
      }}
    >
      <Field label="Title" required>
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          minLength={3}
          placeholder="Pricing for product-led growth"
        />
      </Field>

      <Field label="Description">
        <textarea
          className="input min-h-32"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What attendees will learn, who it's for, anything they should prepare."
        />
      </Field>

      <Field label="Topic tags (up to 6)">
        <TagInput
          value={tags}
          onChange={setTags}
          suggestions={tagSuggestions}
          maxTags={6}
          placeholder={tagSuggestions.length > 0 ? 'Type to filter, or add a new tag' : 'Add a tag and press Enter'}
        />
      </Field>

      <div className="rounded-2xl border border-line bg-canvas/40 p-5">
        <h3 className="text-sm font-semibold tracking-tight">Host</h3>
        <div className="mt-4 flex items-start gap-5">
          <HostHeadshotUploader
            url={hostHeadshotUrl}
            hostName={hostName}
            onChange={setHostHeadshotUrl}
          />
          <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Host name">
              <input
                className="input"
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                required
              />
            </Field>
            <Field label="Host email" required>
              <input
                type="email"
                className="input"
                value={hostEmail}
                onChange={(e) => setHostEmail(e.target.value)}
                required
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Starts at">
          <input
            type="datetime-local"
            className="input"
            value={startsAtLocal}
            onChange={(e) => setStartsAtLocal(e.target.value)}
            required
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            className="input"
            min={15}
            max={480}
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e.target.value))}
            required
          />
        </Field>
      </div>

      <GuestSection state={guest} setState={setGuest} />

      {hideActions ? null : (
        <div className="flex items-center justify-end gap-3 pt-2">
          <a href={cancelHref} className="btn-secondary">Cancel</a>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      )}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </form>
  );
}

function HostHeadshotUploader({
  url,
  hostName,
  onChange,
}: {
  url: string | null;
  hostName: string;
  onChange: (next: string | null) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const handlePick = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url: uploaded } = await api.admin.uploadHostHeadshot(file);
      onChange(uploaded);
    } catch (err) {
      toast.error('Upload failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <Avatar name={hostName || 'Host'} url={url} size={72} />
        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-[11px] font-medium text-white">
            uploading…
          </div>
        ) : null}
      </div>
      <input
        type="file"
        ref={fileInput}
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
      />
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="font-semibold text-accent hover:underline"
          disabled={uploading}
        >
          {url ? 'Replace' : 'Upload headshot'}
        </button>
        {url ? (
          <>
            <span className="text-ink-subtle">·</span>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-ink-subtle hover:text-ink"
              disabled={uploading}
            >
              Remove
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-ink-subtle">
        {label}
        {required ? ' *' : ''}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
